import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  BarChart2,
  Calendar,
  CheckCircle,
  Clock,
  Download,
  LucideAngularModule,
  LogIn,
  LogOut,
  RefreshCw,
  Store as StoreIcon,
  TrendingUp,
  UserCheck,
  UserX,
  Users,
} from 'lucide-angular';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import {
  AttendanceDto,
  EmployeeSummaryDto,
  TodayEmployeeStatusDto,
} from '../../core/models/attendance.models';
import { Store } from '../../core/models/store.models';
import { TranslatePipe } from '@ngx-translate/core';
import { AttendanceService } from '../../core/services/attendance.service';
import { StoreService } from '../../core/services/store.service';
import { AuthService } from '../../core/services/auth.service';
import { StoreLiteDto } from '../../core/models/store.models';
import { storePicker } from '../../core/utils/store-picker';
import { hasAnyPermission } from '../../core/utils/jwt.util';

type ManagerTab = 'today' | 'history' | 'summary';
type EmployeeTab = 'my-today' | 'my-history';

@Component({
  selector: 'app-attendance',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    TranslatePipe,
    DatePickerModule,
    SelectModule,
  ],
  templateUrl: './attendance.component.html',
})
export class AttendanceComponent implements OnInit {
  private attendanceService = inject(AttendanceService);
  private storeService = inject(StoreService);
  private authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  // Icons
  BarChart2 = BarChart2;
  Calendar = Calendar;
  CheckCircle = CheckCircle;
  Clock = Clock;
  Download = Download;
  LogIn = LogIn;
  LogOut = LogOut;
  RefreshCw = RefreshCw;
  StoreIcon = StoreIcon;
  TrendingUp = TrendingUp;
  UserCheck = UserCheck;
  UserX = UserX;
  Users = Users;

  // ── Permission mode ──────────────────────────────────────────────────────────
  isManagerView = computed(() => {
    const role = this.authService.user()?.role;
    if (role === 'Customer') return true;
    const token = this.authService.getAccessToken();
    const perms = this.authService.user()?.permissions;
    return hasAnyPermission(token, role, ['ATTENDANCE_VIEW', 'DASHBOARD_VIEW'], perms);
  });

  canClock = computed(() => {
    const role = this.authService.user()?.role;
    if (role === 'Customer') return false;
    const token = this.authService.getAccessToken();
    const perms = this.authService.user()?.permissions;
    return hasAnyPermission(token, role, ['ATTENDANCE_CLOCK'], perms);
  });

  // ── Manager state ────────────────────────────────────────────────────────────
  managerTab = signal<ManagerTab>('today');

  todayList = signal<TodayEmployeeStatusDto[]>([]);
  todayLoading = signal(false);

  historyList = signal<AttendanceDto[]>([]);
  historyLoading = signal(false);
  historyTotal = signal(0);
  historyPage = signal(1);
  readonly pageSize = 20;

  summaryList = signal<EmployeeSummaryDto[]>([]);
  summaryLoading = signal(false);

  // ── Employee state ───────────────────────────────────────────────────────────
  employeeTab = signal<EmployeeTab>('my-today');

  myToday = signal<AttendanceDto | null>(null);
  myTodayLoading = signal(false);
  clockLoading = signal(false);

  myHistoryList = signal<AttendanceDto[]>([]);
  myHistoryLoading = signal(false);
  myHistoryTotal = signal(0);
  myHistoryPage = signal(1);

  // ── Filters ──────────────────────────────────────────────────────────────────
  stores = signal<Store[]>([]);

  /** Bitta do'konli biznesda filtr yashiriladi — `storePicker` ga qarang. */
  readonly storePick = storePicker(this.stores);
  selectedStoreId: string | null = null;
  selectedDate: Date = new Date();

  // Summary filters — current month
  summaryFrom: Date = (() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  })();
  summaryTo: Date = new Date();
  summaryStoreId: string | null = null;

  // My history filters — current month
  myHistoryFrom: Date = (() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  })();
  myHistoryTo: Date = new Date();

  storeSelectOptions = computed(() => this.stores().map((s) => ({ label: s.name, value: s.id })));

  // ── Computed stats ───────────────────────────────────────────────────────────
  get presentCount() {
    return this.todayList().filter((e) => e.isPresent).length;
  }
  get absentCount() {
    return this.todayList().filter((e) => !e.isPresent).length;
  }

  summaryStats = computed(() => {
    const list = this.summaryList();
    return {
      totalEmployees: list.length,
      avgPresence: list.length
        ? Math.round((list.reduce((s, e) => s + e.daysPresent, 0) / list.length) * 10) / 10
        : 0,
      avgHours: list.length
        ? Math.round((list.reduce((s, e) => s + e.avgHoursPerDay, 0) / list.length) * 10) / 10
        : 0,
      totalHours: Math.round(list.reduce((s, e) => s + e.totalHours, 0) * 10) / 10,
    };
  });

  myStats = computed(() => {
    const list = this.myHistoryList();
    const completed = list.filter((r) => r.checkOutTime);
    const totalMin = completed.reduce((s, r) => {
      const ms = new Date(r.checkOutTime!).getTime() - new Date(r.checkInTime).getTime();
      return s + ms / 60000;
    }, 0);
    return {
      days: new Set(list.map((r) => r.workDate)).size,
      totalHours: Math.round((totalMin / 60) * 10) / 10,
      avgHours: completed.length ? Math.round((totalMin / 60 / completed.length) * 10) / 10 : 0,
    };
  });

  ngOnInit() {
    this.loadStores();
    if (this.isManagerView()) {
      this.loadToday();
    } else {
      this.loadMyToday();
    }
  }

  private loadStores() {
    const role = this.authService.user()?.role;
    if (role === 'Customer') {
      this.storeService
        .getStores()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) => this.stores.set(res.data?.items ?? []),
        });
    } else {
      this.storeService
        .getAllLite()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) => this.stores.set((res.data ?? []).map((s) => this.liteToStore(s))),
        });
    }
  }

  private liteToStore(s: StoreLiteDto): Store {
    return {
      id: s.id,
      name: s.name,
      storeTypeId: s.storeTypeId,
      storeTypeName: s.storeTypeName,
      address: '',
      isActive: true,
      bboxMinLat: null,
      bboxMaxLat: null,
      bboxMinLon: null,
      bboxMaxLon: null,
      hasBbox: false,
    };
  }

  // ── Manager actions ──────────────────────────────────────────────────────────
  switchManagerTab(tab: ManagerTab) {
    this.managerTab.set(tab);
    if (tab === 'today') this.loadToday();
    else if (tab === 'history') this.loadHistory();
    else if (tab === 'summary') this.loadSummary();
  }

  loadToday() {
    this.todayLoading.set(true);
    this.attendanceService
      .getTodayStatus(this.selectedStoreId || undefined)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.todayList.set(res.data ?? []);
          this.todayLoading.set(false);
        },
        error: () => this.todayLoading.set(false),
      });
  }

  private toDateStr(d: Date): string {
    return d.toISOString().slice(0, 10);
  }

  loadHistory() {
    this.historyLoading.set(true);
    this.attendanceService
      .getHistory(
        this.toDateStr(this.selectedDate),
        this.selectedStoreId || undefined,
        this.historyPage(),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.historyList.set(res.data?.items ?? []);
          this.historyTotal.set(res.data?.totalCount ?? 0);
          this.historyLoading.set(false);
        },
        error: () => this.historyLoading.set(false),
      });
  }

  loadSummary() {
    this.summaryLoading.set(true);
    this.attendanceService
      .getSummary(
        this.toDateStr(this.summaryFrom),
        this.toDateStr(this.summaryTo),
        this.summaryStoreId || undefined,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.summaryList.set(res.data ?? []);
          this.summaryLoading.set(false);
        },
        error: () => this.summaryLoading.set(false),
      });
  }

  onFilterChange() {
    const tab = this.managerTab();
    if (tab === 'today') this.loadToday();
    else if (tab === 'history') {
      this.historyPage.set(1);
      this.loadHistory();
    }
  }

  goPage(page: number) {
    this.historyPage.set(page);
    this.loadHistory();
  }
  get totalPages() {
    return Math.max(1, Math.ceil(this.historyTotal() / this.pageSize));
  }

  // ── Employee actions ─────────────────────────────────────────────────────────
  switchEmployeeTab(tab: EmployeeTab) {
    this.employeeTab.set(tab);
    if (tab === 'my-today') this.loadMyToday();
    else if (tab === 'my-history') this.loadMyHistory();
  }

  loadMyToday() {
    this.myTodayLoading.set(true);
    this.attendanceService
      .getMyTodayStatus()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.myToday.set(res.data ?? null);
          this.myTodayLoading.set(false);
        },
        error: () => this.myTodayLoading.set(false),
      });
  }

  loadMyHistory() {
    this.myHistoryLoading.set(true);
    this.attendanceService
      .getMyHistory(
        this.toDateStr(this.myHistoryFrom),
        this.toDateStr(this.myHistoryTo),
        this.myHistoryPage(),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.myHistoryList.set(res.data?.items ?? []);
          this.myHistoryTotal.set(res.data?.totalCount ?? 0);
          this.myHistoryLoading.set(false);
        },
        error: () => this.myHistoryLoading.set(false),
      });
  }

  get myTotalPages() {
    return Math.max(1, Math.ceil(this.myHistoryTotal() / 30));
  }

  checkIn() {
    if (!navigator.geolocation) {
      alert("Geolokatsiya qo'llab-quvvatlanmaydi");
      return;
    }
    this.clockLoading.set(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.attendanceService
          .checkIn({ lat: pos.coords.latitude, lon: pos.coords.longitude })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.clockLoading.set(false);
              this.loadMyToday();
            },
            error: () => this.clockLoading.set(false),
          });
      },
      () => {
        alert('Joylashuvni aniqlashda xato');
        this.clockLoading.set(false);
      },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  }

  checkOut() {
    if (!navigator.geolocation) {
      alert("Geolokatsiya qo'llab-quvvatlanmaydi");
      return;
    }
    this.clockLoading.set(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.attendanceService
          .checkOut({ lat: pos.coords.latitude, lon: pos.coords.longitude })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.clockLoading.set(false);
              this.loadMyToday();
            },
            error: () => this.clockLoading.set(false),
          });
      },
      () => {
        alert('Joylashuvni aniqlashda xato');
        this.clockLoading.set(false);
      },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────
  formatTime(iso: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleTimeString('uz-UZ', { hour: '2-digit', minute: '2-digit' });
  }

  calcDuration(checkIn: string, checkOut: string | null): string {
    if (!checkOut) return '—';
    const diff = Math.floor((new Date(checkOut).getTime() - new Date(checkIn).getTime()) / 60000);
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    return `${h}s ${m}d`;
  }

  getInitials(name: string): string {
    return name
      .trim()
      .split(/\s+/)
      .map((w) => w[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }

  presenceBar(present: number, total: number): number {
    return total > 0 ? Math.round((present / total) * 100) : 0;
  }
}
